import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { markPayoutSent } from "@/lib/admin-referrals.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/vyplaty")({
  component: AdminPayoutsPage,
});

function formatMoney(cents: number, cur: string) {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency: cur }).format(cents / 100);
}

function AdminPayoutsPage() {
  const qc = useQueryClient();
  const mark = useServerFn(markPayoutSent);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "payouts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referral_payouts")
        .select("id, referrer_id, amount_cents, currency, wise_email, status, note, processed_at, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const ids = Array.from(new Set((data ?? []).map((r) => r.referrer_id)));
      let profiles: Record<string, string> = {};
      if (ids.length) {
        const { data: ps } = await supabase
          .from("profiles").select("id, full_name, email").in("id", ids);
        profiles = Object.fromEntries((ps ?? []).map((p: any) => [p.id, p.full_name || p.email || p.id]));
      }
      return (data ?? []).map((r) => ({ ...r, referrer_label: profiles[r.referrer_id] ?? r.referrer_id }));
    },
  });

  const updateMut = useMutation({
    mutationFn: async (v: { id: string; status: "sent" | "failed"; note?: string }) => {
      await mark({ data: v });
    },
    onSuccess: () => {
      toast.success("Stav výplaty aktualizován.");
      qc.invalidateQueries({ queryKey: ["admin", "payouts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Výplaty provizí</h1>
        <p className="text-sm text-muted-foreground">
          Po manuálním odeslání platby přes WISE označte výplatu jako <em>odeslanou</em>.
          Pokud transakce selhala, použijte <em>selhalo</em> a částka se vrátí na zůstatek uživatele.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Uživatel</TableHead>
                <TableHead>WISE e-mail</TableHead>
                <TableHead className="text-right">Částka</TableHead>
                <TableHead>Stav</TableHead>
                <TableHead>Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} className="py-8 text-center">Načítám…</TableCell></TableRow>}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Žádné výplaty.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString("cs-CZ")}</TableCell>
                  <TableCell>{r.referrer_label}</TableCell>
                  <TableCell className="font-mono text-xs">{r.wise_email}</TableCell>
                  <TableCell className="text-right font-medium">{formatMoney(r.amount_cents, r.currency)}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === "sent" ? "default" : r.status === "failed" ? "destructive" : "secondary"}>
                      {r.status}
                    </Badge>
                    {r.note && <div className="mt-1 text-xs text-muted-foreground">{r.note}</div>}
                  </TableCell>
                  <TableCell>
                    {r.status === "queued" ? (
                      <div className="flex flex-col gap-1.5">
                        <Input
                          placeholder="Poznámka / WISE ID"
                          className="h-8 text-xs"
                          value={notes[r.id] ?? ""}
                          onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                        />
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={() => updateMut.mutate({ id: r.id, status: "sent", note: notes[r.id] })}>
                            Odesláno
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => updateMut.mutate({ id: r.id, status: "failed", note: notes[r.id] })}>
                            Selhalo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {r.processed_at ? new Date(r.processed_at).toLocaleString("cs-CZ") : "—"}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
